import { EXPLORE_ROUTES } from "../exploreRoutes";
import { useQuizBrowserBack } from './quizHistoryGuard';
import { QUIZ_ROUTES } from "./quizRoutes";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import QuizzCard from "./QuizzCard";
import StatusFilter from "./StatusFilter";
import TestSeriesCard from "./TestSeriesCard";
import TestSeriesStatusFilter from "./TestSeriesStatusFilter";
import { useQuizFilterList } from "../../hooks/useQuizFilterList";
import { useQuizSeriesList } from "../../hooks/useQuizSeriesList";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../../questionBank/QuestionBankList.css";
import "../../homeDashboard(category)/Categories.css";
import heroImg from "../../assets/images/im4.png";
import {
  buildQuizFlowState,
  getStoredQuizFlowState,
  persistQuizFlowState,
  resolveQuizBackTarget,
  navigateQuizBack,
} from "./quizFlowState";

function buildVisiblePages(currentPage, totalPages) {
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const halfWindow = Math.floor(maxVisiblePages / 2);
  let startPage = Math.max(1, currentPage - halfWindow);
  let endPage = startPage + maxVisiblePages - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = endPage - maxVisiblePages + 1;
  }

  return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
}

function resolveQuizListLabel(routeKey) {
  const normalizedRouteKey = Number(routeKey);

  if (normalizedRouteKey === 2) {
    return "Test Series";
  }

  if (normalizedRouteKey === 4) {
    return "Mock Test";
  }

  if (normalizedRouteKey === 3) {
    return "PY Paper";
  }

  return "Test";
}

function resolveQuizSubtitle(routeKey) {
  if (Number(routeKey) === 2) {
    return "Choose from the latest test series";
  }

  return "Practice more , score higher";
}

const QuizSummaryShimmer = () => (
  <div className="qbl-summaryRow" aria-hidden="true">
    <span className="dashboard-skeleton dashboard-skeleton--title qbl-summaryRow__shimmer-title" />
    <span className="dashboard-skeleton dashboard-skeleton--button qbl-summaryRow__shimmer-guess" />
  </div>
);

function resolveQuizMode(routeKey, routeState) {
  const normalizedRouteKey = Number(routeKey);
  const explicitMode = Number(routeState?.quizRequestParams?.is_quiz);

  if (Number.isFinite(explicitMode)) {
    return explicitMode;
  }

  if (normalizedRouteKey === 2) {
    return 0;
  }

  if (normalizedRouteKey === 4) {
    return 2;
  }

  return 1;
}

const QuizzList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const storedFlowState = useMemo(() => getStoredQuizFlowState(), []);
  const flowState = useMemo(
    () => buildQuizFlowState(location.state, storedFlowState),
    [location.state, storedFlowState]
  );
  const selectedStateName =
    flowState?.selectedState?.state_name || flowState?.stateName || "Himachal Pradesh";
  const selectedQuizOption = flowState?.selectedQuizOption || "Tests & Quizzes";
  const selectedStateId = Number(flowState?.selectedState?.state_id || flowState?.state_id || 13);
  const selectedCategoryId = Number(
    flowState?.category_id ||
      location.state?.quizRequestParams?.category_id ||
      location.state?.selectedQuizOptionData?.category_id ||
      location.state?.selectedCategory?.category_id ||
      1
  );
  const selectedRouteKey = Number(
    flowState?.route_key ||
      location.state?.quizRequestParams?.route_key ||
      location.state?.selectedQuizOptionData?.routeKey ||
      location.state?.selectedQuizOptionData?.route_key ||
      0
  );
  const selectedQuizMode = resolveQuizMode(selectedRouteKey, flowState);
  const [currentPage, setCurrentPage] = useState(Number(flowState.quizListQueryState?.currentPage || 1));
  const [selectedFilters, setSelectedFilters] = useState(flowState.quizListQueryState?.filters || {});
  const listScope = JSON.stringify([selectedCategoryId, selectedQuizMode, selectedRouteKey, selectedStateId]);
  const previousScope = useRef(listScope);

  useEffect(() => {
    if (previousScope.current === listScope) return;
    previousScope.current = listScope;
    setCurrentPage(1);
    setSelectedFilters({});
  }, [listScope]);
  const {
    data: quizFiltersData,
    isLoading: isQuizFiltersLoading,
    isFetching: isQuizFiltersFetching,
  } = useQuizFilterList(
    {
      states_id: selectedStateId,
      category_id: selectedCategoryId,
      route_key: selectedRouteKey,
      is_quiz: selectedQuizMode,
    },
    {
      enabled: Number.isFinite(selectedStateId) && selectedStateId > 0 && Number.isFinite(selectedCategoryId) && selectedCategoryId > 0,
    }
  );
  const topFilters = quizFiltersData?.topFilters || [];
  const quickFilterGroups = quizFiltersData?.quickFilterGroups || [];
  const quizSeriesQueryParams = useMemo(
    () => {
      const { filter_all, ...apiFilters } = selectedFilters;

      return {
        states_id: selectedStateId,
        category_id: selectedCategoryId,
        route_key: selectedRouteKey,
        ...(selectedRouteKey !== 3 ? { is_quiz: selectedQuizMode } : {}),
        page: currentPage,
        ...apiFilters,
      };
    },
    [currentPage, selectedCategoryId, selectedFilters, selectedQuizMode, selectedRouteKey, selectedStateId]
  );
  const {
    data: quizSeriesData,
    isLoading: isQuizSeriesLoading,
    isFetching: isQuizSeriesFetching,
  } = useQuizSeriesList(
    quizSeriesQueryParams,
    {
      enabled:
        Number.isFinite(selectedStateId) &&
        selectedStateId > 0 &&
        Number.isFinite(selectedCategoryId) &&
        selectedCategoryId > 0,
    }
  );

  const handleSelectTopFilter = (filter) => {
    setCurrentPage(1);
    setSelectedFilters((current) => {
      const nextFilters = { ...current };

      if (filter.filterKey === "filter_all") {
        nextFilters.filter_all = filter.value;
        delete nextFilters.free_paid;
        delete nextFilters.filter_time;
        return nextFilters;
      }

      nextFilters[filter.filterKey] = filter.value;
      delete nextFilters.filter_all;
      return nextFilters;
    });
  };

  const handleApplyQuickFilters = (nextQuickFilters = {}) => {
    setCurrentPage(1);
    setSelectedFilters((current) => {
      const nextFilters = { ...current };

      quickFilterGroups.forEach((group) => {
        delete nextFilters[group.filterKey];
      });

      Object.entries(nextQuickFilters).forEach(([filterKey, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          nextFilters[filterKey] = value;
        }
      });

      return nextFilters;
    });
  };

  const handleResetQuickFilters = () => {
    setCurrentPage(1);
    setSelectedFilters((current) => {
      const nextFilters = { ...current };

      quickFilterGroups.forEach((group) => {
        delete nextFilters[group.filterKey];
      });

      return nextFilters;
    });
  };

  const activeFilterCount = useMemo(
    () => Object.keys(selectedFilters).length,
    [selectedFilters]
  );
  const resolvedCurrentPage = currentPage;
  const resolvedTotalPages = Number(quizSeriesData?.totalPages || 1);
  const visiblePages = useMemo(
    () => buildVisiblePages(resolvedCurrentPage, resolvedTotalPages),
    [resolvedCurrentPage, resolvedTotalPages]
  );
  const resolvedStateName = quizSeriesData?.stateName || selectedStateName;
  const isQuizFiltersPending = isQuizFiltersLoading || isQuizFiltersFetching;
  const isQuizListPending = isQuizSeriesLoading || isQuizSeriesFetching;
  const isTestSeriesRoute = selectedRouteKey === 2;
  const startQuizNavigationState = useMemo(() => ({
    selectedState: {
      state_id: selectedStateId,
      state_name: resolvedStateName,
    },
    state_id: selectedStateId,
    stateName: resolvedStateName,
    category_id: selectedCategoryId,
    route_key: selectedRouteKey,
    selectedQuizOption,
    listBannerImage: quizSeriesData?.bannerImage || heroImg,
    quizListQueryState: { currentPage, filters: selectedFilters },
    quizBackTarget: resolveQuizBackTarget(flowState, QUIZ_ROUTES.list, { pathname: EXPLORE_ROUTES.categories, state: {
      selectedState: {
        state_id: selectedStateId,
        state_name: resolvedStateName,
      },
      state_id: selectedStateId,
      stateName: resolvedStateName,
      category_id: selectedCategoryId,
      route_key: selectedRouteKey,
      selectedQuizOption,
      listBannerImage: quizSeriesData?.bannerImage || heroImg,
    } }),
  }), [selectedStateId, resolvedStateName, selectedCategoryId, selectedRouteKey, selectedQuizOption,
    quizSeriesData?.bannerImage, currentPage, selectedFilters, flowState]);
  const categoriesBackTarget = useMemo(
    () => ({
      pathname: EXPLORE_ROUTES.categories,
      state: {
        state_id: selectedStateId,
        selectedState: {
          state_id: selectedStateId,
          state_name: resolvedStateName,
        },
      },
    }),
    [resolvedStateName, selectedStateId]
  );
  const persistedListFlowState = useMemo(
    () =>
      buildQuizFlowState({
        ...flowState,
        ...startQuizNavigationState,
        selectedQuiz: null,
        quizData: {},
        selectedAttempt: null,
        assesment_batch_id: 0,
        resume_on_open: false,
        quizBackTarget: startQuizNavigationState.quizBackTarget,
      }),
    [flowState, startQuizNavigationState]
  );
  const listBackTarget = resolveQuizBackTarget(flowState, QUIZ_ROUTES.list, categoriesBackTarget);
  useQuizBrowserBack(listBackTarget);
  const listLabel = resolveQuizListLabel(selectedRouteKey);
  const subtitleText = resolveQuizSubtitle(selectedRouteKey);
  const summaryText = isQuizSeriesLoading
    ? "Loading quizzes..."
    : `${quizSeriesData?.totalRecords || 0} ${listLabel}${
        Number(quizSeriesData?.totalRecords || 0) === 1 ? "" : "s"
      }`;

  const handlePageChange = (page) => {
    if (page === currentPage || isQuizListPending) {
      return;
    }

    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    persistQuizFlowState(persistedListFlowState);
  }, [persistedListFlowState]);

  return (
    <>
      <style>{`
        .homeDashboard-wrapper .page-content {
          padding-right: 0;
        }
        .homeDashboard-wrapper .main-content {
          background: #ebe9f7;
        }
      `}</style>
      <HomeDashboardLayout showHeader={false}>
        <div className="qbl-page qbankList-page">
          <div className="qbl-main" aria-label="Quiz content">
            <section className="qbl-panel">
              <div className="hideProfile-page">
                <div className="categories-page-header mb-0">
                  <div>
                    <button
                      type="button"
                      className="categories-back-button"
                      onClick={() => {
                        navigateQuizBack(navigate, listBackTarget);
                      }}
                      aria-label="Go back"
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                  </div>
                  <div></div>
                  <div></div>
                </div>
              </div>

              <div className="qb-body">
                <div className="d-flex align-items-center justify-content-center">
                  <div className="qb-copy">
                    <div className="qb-loc">
                      <i className="bi bi-geo-alt-fill" />
                      <span>{resolvedStateName}</span>
                    </div>

                    <h1 className="qb-title">
                      {selectedQuizOption.split(" ").slice(0, -1).join(" ") || selectedQuizOption}{" "}
                      <span>{selectedQuizOption.split(" ").slice(-1).join(" ")}</span>
                    </h1>

                    <p className="qb-sub qbb-list">{subtitleText}</p>
                    {isTestSeriesRoute ? (
                      <TestSeriesStatusFilter
                        topFilters={topFilters}
                        quickFilterGroups={quickFilterGroups}
                        selectedFilters={selectedFilters}
                        onSelectTopFilter={handleSelectTopFilter}
                        onApplyQuickFilters={handleApplyQuickFilters}
                        onResetQuickFilters={handleResetQuickFilters}
                        adCoins={quizSeriesData?.advertEarnedCoins || 0}
                        isLoading={isQuizFiltersPending}
                      />
                    ) : (
                      <StatusFilter
                        topFilters={topFilters}
                        quickFilterGroups={quickFilterGroups}
                        selectedFilters={selectedFilters}
                        onSelectTopFilter={handleSelectTopFilter}
                        onApplyQuickFilters={handleApplyQuickFilters}
                        onResetQuickFilters={handleResetQuickFilters}
                        adCoins={quizSeriesData?.advertEarnedCoins || 0}
                        isLoading={isQuizFiltersPending}
                      />
                    )}
                  </div>

                  <div className="qb-visual-img align-items-center">
                    <img
                      src={quizSeriesData?.bannerImage || heroImg}
                      alt=""
                      className="qb-hero-image"
                    />
                  </div>
                </div>
              </div>

              {isQuizListPending ? (
                <QuizSummaryShimmer />
              ) : (
                <div className="qbl-summaryRow">
                  <h2>{summaryText}</h2>
                  {!isQuizSeriesLoading && activeFilterCount > 0 ? (
                    <span>{activeFilterCount} Active Filter{activeFilterCount === 1 ? "" : "s"}</span>
                  ) : null}
                </div>
              )}

              <div className="qbl-questionList" aria-label="Quiz list">
                {isTestSeriesRoute ? (
                  <TestSeriesCard
                    items={quizSeriesData?.quizList || []}
                    isLoading={isQuizListPending}
                    navigationState={startQuizNavigationState}
                    emptyMessage="No items available for the selected filters."
                  />
                ) : (
                  <QuizzCard
                    coinBalance={quizSeriesData?.advertEarnedCoins || 0}
                    separateToday={selectedRouteKey === 1 || selectedRouteKey === 4}
                    items={quizSeriesData?.quizList || []}
                    isLoading={isQuizListPending}
                    navigationState={startQuizNavigationState}
                    emptyMessage={
                      selectedRouteKey === 1 || selectedRouteKey === 3 || selectedRouteKey === 4
                        ? "No items available for the selected filters."
                        : "This route does not have a dynamic list implementation yet."
                    }
                  />
                )}
              </div>

              {!isQuizListPending && resolvedTotalPages > 1 ? (
                <div className="qbl-pagination">
                  <button
                    type="button"
                    className="qbl-pagination__nav"
                    disabled={resolvedCurrentPage <= 1}
                    onClick={() => handlePageChange(Math.max(1, resolvedCurrentPage - 1))}
                  >
                    Previous
                  </button>
                  <div className="qbl-pagination__pages">
                    {visiblePages.map((pageNumber) => (
                      <button
                        key={pageNumber}
                        type="button"
                        className={`qbl-pagination__page${
                          pageNumber === resolvedCurrentPage ? " is-active" : ""
                        }`}
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="qbl-pagination__nav"
                    disabled={resolvedCurrentPage >= resolvedTotalPages}
                    onClick={() =>
                      handlePageChange(Math.min(resolvedTotalPages, resolvedCurrentPage + 1))
                    }
                  >
                    Next
                  </button>
                </div>
              ) : null}
            </section>
          </div>
        </div>
      </HomeDashboardLayout>
    </>
  );
};

export default QuizzList;








